<template>
  <v-skeleton-loader :loading="showLoader" type="card-avatar, article" style="background-color: transparent">
    <v-card width="100%">
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <!-- Show uploaded file preview OR textarea -->
            <div v-if="uploadedFile" class="d-flex align-center pa-4 bg-white rounded">
              <v-icon color="primary" class="mr-2">mdi-file-document</v-icon>
              <span class="text-primary">{{ uploadedFile.fileName }}</span>
              <v-spacer></v-spacer>
              <v-btn icon size="small" @click="clearUploadedFile">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
            <v-textarea v-else v-model="newNote" label="Notes" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="auto">
            <v-btn :disabled="(!newNote || newNote.trim() === '') && !uploadedFile" @click="addNote">{{
              addNoteButtonLabel
            }}</v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <!-- File upload toggle -->
            <v-btn-lh v-if="!showFileUpload && !uploadedFile" @click="showFileUpload = true"> Attach a File </v-btn-lh>

            <!-- Upload component -->
            <UploadFileComponent v-if="showFileUpload" @download-link="onDownloadLink" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-skeleton-loader>
</template>

<script setup>
import { ref } from "vue";
import { userNotesService } from "@/services/userNotesService";
import UploadFileComponent from "@/components/UploadFileComponent.vue";

const showLoader = ref(false);
const newNote = ref("");
const errorMsg = ref("");
const showFileUpload = ref(false);
const uploadedFile = ref(null);

const onDownloadLink = (download) => {
  uploadedFile.value = { link: download.link, fileName: download.fileName };
  showFileUpload.value = false;
};

const clearUploadedFile = () => {
  uploadedFile.value = null;
};

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  orderInfoId: {
    type: String,
    required: false,
    default: null,
  },
  intakeOrderId: {
    type: Number,
    required: false,
    default: null,
  },
  intakeDocumentId: {
    type: String,
    required: false,
    default: null,
  },
  onAddNote: {
    type: [Function, null],
    required: false,
    default: null,
  },
  overrideAddNote: {
    type: [Function, null],
    required: false,
    default: null,
  },
  addNoteButtonLabel: {
    type: String,
    required: false,
    default: "Add Note",
  },
});

const addNote = async () => {
  try {
    let noteContent;

    if (uploadedFile.value) {
      // Store file as: [FILE:filename](url)
      noteContent = `[FILE:${uploadedFile.value.fileName}](${uploadedFile.value.link})`;
    } else {
      noteContent = newNote.value.trim();
    }

    if (!noteContent) return;

    if (props.overrideAddNote) {
      await props.overrideAddNote(noteContent, {
        orderInfoId: props.orderInfoId,
        intakeOrderId: props.intakeOrderId,
        intakeDocumentId: props.intakeDocumentId,
      });
      newNote.value = "";
      uploadedFile.value = null;
      return;
    }
    errorMsg.value = "";
    showLoader.value = true;
    const createdNote = await userNotesService.createNote({
      orderInfo: props.orderInfoId,
      intakeOrder: props.intakeOrderId,
      intakeDocument: props.intakeDocumentId,
      note: noteContent,
    });
    newNote.value = "";
    uploadedFile.value = null;
    if (props.onAddNote) {
      props.onAddNote(createdNote);
    }
  } catch (e) {
    errorMsg.value = `Failed to add note: ${e.message}`;
  } finally {
    showLoader.value = false;
  }
};
</script>
